import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, Divider, Grid, MenuItem, Paper, Stack,
  TextField, Typography,
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import { apiFetch } from '../../utils/apiFetch.js';
import { mapPricingPayload } from './onboardingMapping.js';

const MOVE_SIZES = {
  studio: { label: 'Studio / 1BR', boxes: 10 },
  '2br': { label: '2 Bedroom', boxes: 20 },
  '3br': { label: '3 Bedroom', boxes: 35 },
};

const money = n => new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(Number(n) || 0);

export default function PricingPreview({ answers, token, pricingStatus }) {
  const [scenario, setScenario] = useState({
    moveSize: '2br', distanceKm: 50, crewSize: answers.crews.crewSizes[0] || 2,
    estimatedHours: 4, packing: false,
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!answers.crews.crewSizes.includes(scenario.crewSize)) {
      setScenario(s => ({ ...s, crewSize: answers.crews.crewSizes[0] || 2 }));
    }
  }, [answers.crews.crewSizes]);

  const run = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const headers = token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {};
      const longDistance = answers.services.longDistance && Number(scenario.distanceKm) >= 50;
      const response = await apiFetch('/api/moverPricing/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({
          pricing: mapPricingPayload(answers),
          scenario: {
            origin: answers.serviceArea.homeCity || 'Calgary, AB',
            destination: longDistance ? 'Vancouver, BC' : (answers.serviceArea.localCities[0] || answers.serviceArea.homeCity || 'Calgary, AB'),
            distanceKm: Number(scenario.distanceKm) || 0,
            crewSize: scenario.crewSize,
            estimatedHours: Number(scenario.estimatedHours) || undefined,
            boxCount: MOVE_SIZES[scenario.moveSize].boxes,
            packingRequired: scenario.packing && answers.services.packing,
          },
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Preview failed');
      if (!data.ok) throw new Error(data.error || 'Your pricing is missing information needed for an estimate.');
      setResult(data.preview);
      if (data.pricingStatus && !data.pricingStatus.valid) {
        setError(`Heads up: ${data.pricingStatus.missingFields?.length ? `missing ${data.pricingStatus.missingFields.join(', ')}` : 'pricing incomplete'} on your saved configuration.`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const breakdown = result?.breakdown || {};
  const total = result?.total;
  const flatFeesTotal = (breakdown.flatFees || []).reduce((sum, fee) => sum + (Number(fee.amount) || 0), 0);
  const surchargesTotal = (breakdown.surcharges || []).reduce((sum, s) => sum + (Number(String(s).replace(/[^0-9.]/g, '')) || 0), 0);

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#F8FAFC' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <CalculateIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>Test your pricing</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        See what a customer would pay for an example move, calculated by MOVD's real pricing engine.
      </Typography>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <TextField fullWidth select size="small" label="Home size" value={scenario.moveSize}
            onChange={e => setScenario(s => ({ ...s, moveSize: e.target.value }))}>
            {Object.entries(MOVE_SIZES).map(([id, s]) => <MenuItem key={id} value={id}>{s.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField fullWidth select size="small" label="Crew" value={scenario.crewSize}
            onChange={e => setScenario(s => ({ ...s, crewSize: Number(e.target.value) }))}>
            {(answers.crews.crewSizes.length ? answers.crews.crewSizes : [2]).map(size => (
              <MenuItem key={size} value={size}>{size === 5 ? '5+' : size} movers</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField fullWidth size="small" type="number" label="Distance (km)" inputProps={{ min: 1 }}
            value={scenario.distanceKm} onChange={e => setScenario(s => ({ ...s, distanceKm: e.target.value }))} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField fullWidth size="small" type="number" label="Hours" inputProps={{ min: 1 }}
            value={scenario.estimatedHours} onChange={e => setScenario(s => ({ ...s, estimatedHours: e.target.value }))} />
        </Grid>
        {answers.services.packing && (
          <Grid item xs={12} sm={2}>
            <TextField fullWidth select size="small" label="Packing" value={scenario.packing ? 'yes' : 'no'}
              onChange={e => setScenario(s => ({ ...s, packing: e.target.value === 'yes' }))}>
              <MenuItem value="no">No</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
            </TextField>
          </Grid>
        )}
      </Grid>

      <Button variant="contained" onClick={run} disabled={loading}>
        {loading ? 'Calculating…' : 'Calculate example price'}
      </Button>

      {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}

      {result && (
        <Box sx={{ mt: 2.5 }}>
          <Typography variant="overline" color="text.secondary">ESTIMATED CUSTOMER PRICE</Typography>
          <Typography variant="h4" fontWeight={800} color="primary.main">{money(total)}</Typography>
          <Divider sx={{ my: 1.5 }} />
          <Stack spacing={0.5}>
            {breakdown.baseFee > 0 && <Row label="Base fee" value={breakdown.baseFee} />}
            {breakdown.laborCost?.total > 0 && <Row label={`Labor (${breakdown.laborCost.estimatedHours}h @ ${money(breakdown.laborCost.hourlyRate)}/hr)`} value={breakdown.laborCost.total} />}
            {breakdown.distanceCost > 0 && <Row label={`Distance (${breakdown.distanceKm} km)`} value={breakdown.distanceCost} />}
            {breakdown.itemCost > 0 && <Row label={`Items (${breakdown.itemCount})`} value={breakdown.itemCost} />}
            {surchargesTotal > 0 && <Row label="Additional charges" value={surchargesTotal} />}
            {flatFeesTotal > 0 && <Row label="Fees" value={flatFeesTotal} />}
            {breakdown.taxes > 0 && <Row label="Tax" value={breakdown.taxes} />}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Example only — actual customer quotes depend on the real move details.
          </Typography>
        </Box>
      )}

      {pricingStatus && !pricingStatus.valid && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Pricing setup is incomplete{pricingStatus.missingFields?.length ? `: missing ${pricingStatus.missingFields.join(', ')}` : ''}.
        </Alert>
      )}
    </Paper>
  );
}

function Row({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{money(value)}</Typography>
    </Box>
  );
}
